/**
 * Backup Sync Service - Simple & Robust
 * 
 * Philosophy: Either backups are done or they're not.
 * 
 * How it works:
 * 1. Count records in IndexedDB
 * 2. Check last backup count
 * 3. If records > last backup → backup needed
 * 4. Check this on app load, login, periodically - doesn't matter when
 * 
 * No dependency on:
 * - Upload timing
 * - Session state during upload
 * - Heartbeats
 * - Complex triggers
 * 
 * Just simple math: Do we have unbackuped records? Yes/No.
 */

import localforage from 'localforage';
import apiService from './apiService';
import healthStorage from './healthStorage';

// Simple state store
const backupStateStore = localforage.createInstance({
  name: 'aidoctorben',
  storeName: 'backup_sync'
});

/**
 * Get backup sync status
 * Returns simple numbers: what we have vs what's backed up
 */
export const getBackupStatus = async () => {
  try {
    // Get current data from IndexedDB
    const records = await healthStorage.getAllHealthRecords();
    const conversations = await healthStorage.getAllConversations();
    
    // Get last backup state (now tracks IDs, not just counts)
    const lastBackup = await backupStateStore.getItem('last_backup') || {
      record_ids: [],
      conversation_ids: [],
      timestamp: null,
      backup_id: null
    };
    
    // Extract current IDs
    const currentRecordIds = records.map(r => r.id);
    const currentConversationIds = conversations.map(c => c.id);
    
    // Find NEW items (IDs not in last backup)
    const backedUpRecordIds = lastBackup.record_ids || [];
    const backedUpConversationIds = lastBackup.conversation_ids || [];
    
    const newRecordIds = currentRecordIds.filter(id => !backedUpRecordIds.includes(id));
    const newConversationIds = currentConversationIds.filter(id => !backedUpConversationIds.includes(id));
    
    const status = {
      // Current state
      current_records: records.length,
      current_conversations: conversations.length,
      
      // Last backup state
      backed_up_records: backedUpRecordIds.length,
      backed_up_conversations: backedUpConversationIds.length,
      last_backup_time: lastBackup.timestamp,
      
      // ID-based tracking (P1 fix - Jan 27, 2026)
      new_record_ids: newRecordIds,
      new_conversation_ids: newConversationIds,
      unbackuped_records: newRecordIds.length,
      unbackuped_conversations: newConversationIds.length,
      
      // Simple boolean - backup needed if ANY new items
      needs_backup: newRecordIds.length > 0 || newConversationIds.length > 0
    };
    
    console.log('[BackupSync] Status (ID-based):', {
      records: `${status.unbackuped_records} new of ${status.current_records}`,
      conversations: `${status.unbackuped_conversations} new of ${status.current_conversations}`,
      needs_backup: status.needs_backup
    });
    return status;
  } catch (error) {
    console.error('[BackupSync] Error getting status:', error);
    return { needs_backup: false, error: error.message };
  }
};

/**
 * Mark backup as complete
 * Called after successful backup email sent
 * Updates BOTH the sync state AND the user-visible backup metadata
 * Now uses ID-based tracking (P1 fix - Jan 27, 2026)
 */
export const markBackupComplete = async (recordCount, conversationCount) => {
  // Get current IDs to store
  const records = await healthStorage.getAllHealthRecords();
  const conversations = await healthStorage.getAllConversations();
  
  const backupState = {
    // ID-based tracking (replaces count-based)
    record_ids: records.map(r => r.id),
    conversation_ids: conversations.map(c => c.id),
    // Legacy fields for backwards compatibility
    record_count: recordCount,
    conversation_count: conversationCount,
    timestamp: new Date().toISOString(),
    backup_id: `backup_${Date.now()}`
  };
  
  // Update sync state (for determining when next backup is needed)
  await backupStateStore.setItem('last_backup', backupState);
  
  // Also update user preferences (for UI display in BackupManager)
  // This ensures the Profile page shows the correct "Last backup" date
  await healthStorage.setLastBackupDate();
  
  console.log('[BackupSync] Backup marked complete (ID-based):', {
    records: backupState.record_ids.length,
    conversations: backupState.conversation_ids.length
  });
  return backupState;
};

/**
 * Main function: Check if backup needed and do it
 * 
 * Call this anywhere:
 * - On app load
 * - On login
 * - On dashboard mount
 * - Periodically
 * 
 * It's idempotent - safe to call multiple times
 */
export const syncBackupIfNeeded = async (sessionToken) => {
  try {
    console.log('[BackupSync] Starting sync check...');
    
    // Step 1: Check status
    const status = await getBackupStatus();
    console.log('[BackupSync] Status check result:', {
      current_records: status.current_records,
      backed_up_records: status.backed_up_records,
      needs_backup: status.needs_backup
    });
    
    if (!status.needs_backup) {
      console.log('[BackupSync] All records backed up ✓');
      return { triggered: false, reason: 'up_to_date' };
    }
    
    if (!sessionToken) {
      console.log('[BackupSync] Backup needed but no session token - will retry later');
      return { triggered: false, reason: 'no_session' };
    }
    
    console.log(`[BackupSync] ${status.unbackuped_records} records need backup, session token present: ${sessionToken.substring(0, 10)}...`);
    
    // Step 2: Get all data
    const records = await healthStorage.getAllHealthRecords();
    const conversations = await healthStorage.getAllConversations();
    const moodCheckins = await healthStorage.getMoodCheckins?.() || [];
    const preferences = await healthStorage.getUserPreferences();
    
    // Step 3: Get only NEW records since last backup (incremental)
    // Sort by createdAt descending, take only the unbackuped count
    const sortedRecords = [...records].sort((a, b) => 
      new Date(b.createdAt) - new Date(a.createdAt)
    );
    const newRecords = sortedRecords.slice(0, status.unbackuped_records);
    
    // Same for conversations
    const sortedConversations = [...conversations].sort((a, b) =>
      new Date(b.timestamp) - new Date(a.timestamp)
    );
    const newConversations = sortedConversations.slice(0, status.unbackuped_conversations);
    
    console.log(`[BackupSync] Sending ${newRecords.length} new records, ${newConversations.length} new conversations`);
    
    // ========================================
    // PRE-FLIGHT VALIDATION
    // Check before sending, not after failing
    // ========================================
    
    // Calculate approximate size of what we're about to send
    const estimatedSize = newRecords.reduce((total, record) => {
      const fileSize = record.fileData ? record.fileData.length : 0;
      return total + fileSize;
    }, 0);
    
    const estimatedSizeMB = estimatedSize / (1024 * 1024);
    const MAX_SIZE_MB = 25; // Mailgun limit
    
    console.log(`[BackupSync] Pre-flight check:
      - Expected records: ${status.unbackuped_records}
      - Sending records: ${newRecords.length}
      - Estimated size: ${estimatedSizeMB.toFixed(2)}MB
      - Max allowed: ${MAX_SIZE_MB}MB`);
    
    // Validation 1: Record count matches expectation
    if (newRecords.length !== status.unbackuped_records) {
      console.warn(`[BackupSync] Warning: Record count mismatch. Expected ${status.unbackuped_records}, got ${newRecords.length}`);
    }
    
    // Validation 2: Size is within limits
    if (estimatedSizeMB > MAX_SIZE_MB) {
      console.error(`[BackupSync] Payload too large: ${estimatedSizeMB.toFixed(2)}MB exceeds ${MAX_SIZE_MB}MB limit`);
      return {
        triggered: true,
        success: false,
        error: `Backup too large (${estimatedSizeMB.toFixed(1)}MB). Please use Download Backup for full data.`
      };
    }
    
    // Validation passed - proceed with send
    console.log('[BackupSync] Pre-flight passed ✓');
    
    const backup = {
      backup_metadata: {
        version: '2.4-incremental',
        backup_id: `backup_${Date.now()}`,
        backup_sequence: Math.floor(records.length / 3) + 1,
        previous_backup_id: null,
        created_at: new Date().toISOString(),
        app_version: '1.0.0',
        trigger: 'sync_check',
        threshold_at_time: 3,
        is_incremental: true,
        records_validated: true,
        estimated_size_mb: estimatedSizeMB.toFixed(2)
      },
      backup_stats: {
        total_records: records.length,
        total_conversations: conversations.length,
        total_mood_checkins: moodCheckins.length,
        records_in_this_backup: newRecords.length,
        conversations_in_this_backup: newConversations.length,
        records_since_last_backup: status.unbackuped_records,
        conversations_since_last_backup: status.unbackuped_conversations
      },
      data: {
        health_records: newRecords,
        conversations: newConversations,
        mood_checkins: moodCheckins,
        user_preferences: preferences
      }
    };
    
    // Step 4: Send backup
    console.log('[BackupSync] Sending backup email...');
    const result = await apiService.sendBackupEmail(backup, sessionToken);
    
    if (result.success) {
      // Step 5: Mark complete
      await markBackupComplete(records.length, conversations.length);
      console.log('[BackupSync] Backup sent successfully ✓');
      return { 
        triggered: true, 
        success: true, 
        records_backed_up: newRecords.length,
        backupSequence: backup.backup_metadata.backup_sequence,
        totalRecords: backup.backup_stats.total_records
      };
    } else {
      console.error('[BackupSync] Backup send failed:', result.message);
      return { triggered: true, success: false, error: result.message };
    }
    
  } catch (error) {
    console.error('[BackupSync] Error:', error);
    return { triggered: false, error: error.message };
  }
};

/**
 * Force a backup regardless of status
 * For manual "Backup Now" button
 * Sends only NEW records since last backup (incremental)
 * Full images included since we're only sending a few records
 */
export const forceBackup = async (sessionToken) => {
  if (!sessionToken) {
    return { success: false, error: 'Not logged in' };
  }
  
  // Get current status to know what's new
  const status = await getBackupStatus();
  
  const records = await healthStorage.getAllHealthRecords();
  const conversations = await healthStorage.getAllConversations();
  const moodCheckins = await healthStorage.getMoodCheckins?.() || [];
  const preferences = await healthStorage.getUserPreferences();
  
  // Get only NEW records since last backup (incremental)
  const sortedRecords = [...records].sort((a, b) => 
    new Date(b.createdAt) - new Date(a.createdAt)
  );
  const newRecords = status.unbackuped_records > 0 
    ? sortedRecords.slice(0, status.unbackuped_records)
    : sortedRecords.slice(0, 3); // If nothing new, send last 3 as confirmation
  
  const sortedConversations = [...conversations].sort((a, b) =>
    new Date(b.timestamp) - new Date(a.timestamp)
  );
  const newConversations = status.unbackuped_conversations > 0
    ? sortedConversations.slice(0, status.unbackuped_conversations)
    : [];
  
  // ========================================
  // PRE-FLIGHT VALIDATION
  // ========================================
  const estimatedSize = newRecords.reduce((total, record) => {
    const fileSize = record.fileData ? record.fileData.length : 0;
    return total + fileSize;
  }, 0);
  
  const estimatedSizeMB = estimatedSize / (1024 * 1024);
  const MAX_SIZE_MB = 25;
  
  console.log(`[BackupSync] Pre-flight check (manual):
    - Records to send: ${newRecords.length}
    - Estimated size: ${estimatedSizeMB.toFixed(2)}MB
    - Max allowed: ${MAX_SIZE_MB}MB`);
  
  if (estimatedSizeMB > MAX_SIZE_MB) {
    console.log(`[BackupSync] Backup size ${estimatedSizeMB.toFixed(1)}MB exceeds email limit - directing to Download`);
    return {
      success: false,
      message: `Your backup is ${estimatedSizeMB.toFixed(0)}MB - too large for email (max ${MAX_SIZE_MB}MB). Use 'Download Backup' instead - it works for any size!`
    };
  }
  
  console.log('[BackupSync] Pre-flight passed ✓');
  
  const backup = {
    backup_metadata: {
      version: '2.4-incremental',
      backup_id: `backup_${Date.now()}`,
      backup_sequence: Math.floor(records.length / 3) + 1,
      previous_backup_id: null,
      created_at: new Date().toISOString(),
      app_version: '1.0.0',
      trigger: 'manual',
      threshold_at_time: 0,
      is_incremental: true,
      records_validated: true,
      estimated_size_mb: estimatedSizeMB.toFixed(2)
    },
    backup_stats: {
      total_records: records.length,
      total_conversations: conversations.length,
      total_mood_checkins: moodCheckins.length,
      records_in_this_backup: newRecords.length,
      conversations_in_this_backup: newConversations.length
    },
    data: {
      health_records: newRecords,
      conversations: newConversations,
      mood_checkins: moodCheckins,
      user_preferences: preferences
    }
  };
  
  const result = await apiService.sendBackupEmail(backup, sessionToken);
  
  if (result.success) {
    await markBackupComplete(records.length, conversations.length);
  }
  
  return {
    ...result,
    backupSequence: backup.backup_metadata.backup_sequence,
    totalRecords: backup.backup_stats.total_records
  };
};

/**
 * Reset backup state after restore
 * Called after user restores from a backup file
 * Marks all CURRENT records as already backed up (since they came from a backup)
 * Added Jan 29, 2026
 */
export const markRestoredAsBackedUp = async () => {
  const records = await healthStorage.getAllHealthRecords();
  const conversations = await healthStorage.getAllConversations();
  
  const backupState = {
    record_ids: records.map(r => r.id),
    conversation_ids: conversations.map(c => c.id),
    timestamp: new Date().toISOString(),
    backup_id: `restore_${Date.now()}`
  };
  
  await backupStateStore.setItem('last_backup', backupState);
  console.log('[BackupSync] Restored data marked as backed up:', {
    records: backupState.record_ids.length,
    conversations: backupState.conversation_ids.length
  });
  return backupState;
};

export default {
  getBackupStatus,
  markBackupComplete,
  syncBackupIfNeeded,
  forceBackup,
  markRestoredAsBackedUp
};
